import React, { useEffect, useRef, useState } from 'react';
import {
  CALLOUT_FONT,
  CALLOUT_LINE_HEIGHT_PX,
  CALLOUT_MAX_CHARS,
  CALLOUT_MAX_WIDTH_PX,
  CALLOUT_PADDING_PX,
} from '../../utils/calloutPlan';

/**
 * Typing a note's text, over the plan, where the cloud is going to be.
 *
 * An HTML field rather than text editing on the canvas: the third step of the
 * tool is "type the text", and a real input is the only thing that gives you
 * an IME, a caret, selection, undo, and a paste — all of which a hand-rolled
 * canvas editor would have to reinvent badly.
 *
 * Sized and typeset to match `drawCallout`, so committing does not visibly
 * reflow what you just typed.
 *
 * Enter commits (Shift+Enter is a newline, since a note is often two lines),
 * Escape abandons, and blur commits — clicking away from a note you have
 * written should keep it, not throw it away.
 */
const CalloutTextEditor = ({
  left,
  top,
  initialText = '',
  onCommit,
  onCancel,
}) => {
  const [text, setText] = useState(initialText);
  const ref = useRef(null);
  // Escape must not also fire the blur-commit on the way out.
  const resolved = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  }, []);

  const commit = () => {
    if (resolved.current) return;
    resolved.current = true;
    onCommit?.(text);
  };
  const cancel = () => {
    if (resolved.current) return;
    resolved.current = true;
    onCancel?.();
  };

  return (
    <textarea
      ref={ref}
      value={text}
      maxLength={CALLOUT_MAX_CHARS}
      rows={2}
      placeholder="Note…"
      aria-label="Note text"
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        // The canvas is listening for Escape and Delete too; this field owns
        // them while it is up.
        e.stopPropagation();
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          commit();
        } else if (e.key === 'Escape') {
          e.preventDefault();
          cancel();
        }
      }}
      // The canvas would otherwise read a click in the field as a plan click.
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        left,
        top,
        // Centred on the cloud's point, like the cloud itself.
        transform: 'translate(-50%, -50%)',
        width: CALLOUT_MAX_WIDTH_PX + CALLOUT_PADDING_PX * 2,
        padding: CALLOUT_PADDING_PX,
        font: CALLOUT_FONT,
        lineHeight: `${CALLOUT_LINE_HEIGHT_PX}px`,
        color: '#2E1065',
        backgroundColor: '#FFFFFF',
        border: '2px solid var(--color-admin, #7C3AED)',
        borderRadius: 10,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.18)',
        resize: 'none',
        outline: 'none',
        zIndex: 40,
      }}
    />
  );
};

export default CalloutTextEditor;
